import React, { useState } from "react";
import { Clock, X, Check, Loader2 } from "lucide-react";

const DAYS = [["monday", "Pon"], ["tuesday", "Uto"], ["wednesday", "Sre"], ["thursday", "Čet"],
  ["friday", "Pet"], ["saturday", "Sub"], ["sunday", "Ned"]];

const blank = () => Object.fromEntries(DAYS.map(([k]) => [k, { open: "07:00", close: "11:30", closed: true }]));

/** Current, server-computed state of a category's recurring availability window. */
export function ScheduleBadge({ status, testid }) {
  if (!status?.enabled) return null;
  const on = status.open_now;
  return (
    <span data-testid={testid}
      className={`text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0 ${on ? "bg-brand-green/15 text-brand-green" : "bg-slate-200 text-slate-500"}`}>
      {on ? "Dostupno sada" : "Van termina"}{status.today ? ` · ${status.today}` : ""}
    </span>
  );
}

/**
 * Recurring per-day availability for ONE category (e.g. Doručak Pon–Pet 07:00–11:30).
 * Times are configured per restaurant; start is inclusive and end exclusive, and an end before the
 * start (22:00–02:00) is a valid overnight window — the backend validates and decides.
 */
export function CategoryScheduleEditor({ category, onClose, onSave }) {
  const [enabled, setEnabled] = useState(!!category.availability_enabled);
  const [hours, setHours] = useState(() => ({ ...blank(), ...(category.availability_hours || {}) }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (day, patch) => setHours((h) => ({ ...h, [day]: { ...h[day], ...patch } }));
  const applyToAll = (day) => setHours((h) => Object.fromEntries(
    DAYS.map(([k]) => [k, { ...h[k], open: h[day].open, close: h[day].close, closed: h[day].closed }])));

  const save = async () => {
    if (saving) return;
    setErr("");
    setSaving(true);
    const res = await onSave(enabled ? { availability_enabled: true, availability_hours: hours }
                                     : { availability_enabled: false });
    setSaving(false);
    if (res?.error) setErr(res.error);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="category-schedule-editor">
      <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Clock size={18} />Vremenska dostupnost</h3>
          <button data-testid="schedule-close" onClick={onClose} className="text-slate-400"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">{category.name} — kategorija se naručuje samo u izabranim terminima.</p>

        <label className="flex items-center justify-between rounded-xl border-2 border-slate-200 px-4 py-3 mb-3">
          <span className="text-sm font-bold">Ograniči na termine</span>
          <input data-testid="schedule-enabled" type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)} className="w-5 h-5 accent-current text-brand-red" />
        </label>

        {enabled && (
          <div className="space-y-1.5">
            {DAYS.map(([key, label]) => (
              <div key={key} className="flex items-center gap-2" data-testid={`schedule-day-${key}`}>
                <label className="w-20 flex items-center gap-1.5 text-sm font-bold">
                  <input data-testid={`schedule-day-on-${key}`} type="checkbox" checked={!hours[key].closed}
                    onChange={(e) => set(key, { closed: !e.target.checked })} className="w-4 h-4" />
                  {label}
                </label>
                <input data-testid={`schedule-open-${key}`} type="time" value={hours[key].open || ""} disabled={hours[key].closed}
                  onChange={(e) => set(key, { open: e.target.value })}
                  className="flex-1 min-w-0 rounded-lg border-2 border-slate-200 px-2 py-2 text-sm disabled:opacity-40" />
                <span className="text-slate-400 text-sm">–</span>
                <input data-testid={`schedule-close-${key}`} type="time" value={hours[key].close || ""} disabled={hours[key].closed}
                  onChange={(e) => set(key, { close: e.target.value })}
                  className="flex-1 min-w-0 rounded-lg border-2 border-slate-200 px-2 py-2 text-sm disabled:opacity-40" />
                <button data-testid={`schedule-apply-all-${key}`} onClick={() => applyToAll(key)} title="Primeni na sve dane"
                  className="text-[10px] font-black text-brand-red shrink-0">SVI</button>
              </div>
            ))}
            <p className="text-[11px] text-slate-400 pt-1">
              Početak je uključen, kraj isključen (07:00–11:30 znači da u 11:30 kategorija više nije dostupna).
              Kraj pre početka (22:00–02:00) znači termin preko ponoći.
            </p>
          </div>
        )}

        {err && <p data-testid="schedule-error" className="text-xs font-bold text-brand-red mt-3">{err}</p>}
        <button data-testid="schedule-save" onClick={save} disabled={saving}
          className="w-full mt-4 rounded-xl bg-brand-red hover:bg-brand-redHover text-white py-3 font-display font-black flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <><Loader2 size={16} className="animate-spin" />Čuvanje...</> : <><Check size={16} />SAČUVAJ</>}
        </button>
      </div>
    </div>
  );
}
